// ** React Imports
import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

// ** Logic Imports
import { GetTemplate } from "./GetTemplate";
import { GetNotification } from "./GetNotifications";
import { MarkAsRead } from "./MarkAsRead";

// ** Hook Imports
import useSignalR from "hooks/useSignalR";

export const useNotificationLogic = () => {
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState([]);
  const [allNotifications, setAllNotifications] = useState([]);
  const [unreadNotifications, setUnreadNotifications] = useState([]);
  const [readNotifications, setReadNotifications] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [placeholders, setPlaceholders] = useState([]);
  const [selectedButton, setSelectedButton] = useState(0);
  const [selectedNotifications, setSelectedNotifications] = useState([]);
  const [isAllSelected, setIsAllSelected] = useState(false);

  const processSingleNotification = useCallback((placeholder) => {
    if (!templates.length) {
      return {
        ...placeholder,
        title: "Notification",
        description: "Notification content not available",
        isProcessed: false,
      };
    }

    const template = templates.find(
      (t) => t.notificationID === placeholder.notificationID
    );

    if (!template) {
      return {
        ...placeholder,
        title: "Notification",
        description: "Notification content not available",
        isProcessed: false,
      };
    }

    const title = replacePlaceholders(template.titleTemplate, placeholder);
    const description = replacePlaceholders(
      template.descriptionTemplate,
      placeholder
    );

    return {
      ...placeholder,
      title,
      description,
      rawDescription: description,
      templateDetails: template,
      isProcessed: true,
      triggerType: template.triggerType,
      createDate: placeholder.createDate || new Date().toISOString(),
    };
  }, [templates]);

  const handleNewNotification = useCallback((newNotification) => {
      const processedNotification = processSingleNotification(newNotification);

      setAllNotifications((prev) => {
        if (
          prev.some(
            (n) =>
              n.userNotificationID === processedNotification.userNotificationID
          )
        ) {
          return prev;
        }
        return [processedNotification, ...prev];
      });

      if (!processedNotification.isRead) {
        setUnreadNotifications((prev) => [processedNotification, ...prev]);
        if (selectedButton === 0) {
          setNotifications((prev) => [processedNotification, ...prev]);
        }
      }
    },
    [processSingleNotification]
  );

  const { connection, connectionStatus } = useSignalR(handleNewNotification);

  useEffect(() => {
    const getTemplates = async () => {
      try {
        const result = await GetTemplate(navigate);
        if (result) {
          setTemplates(result);
        }
      } catch (error) {
        console.error(error || "Error fetching notification templates.");
      }
    };

    getTemplates();
  }, []);

  const getNotifications = async () => {
    try {
      const result = await GetNotification(1, 20, navigate);

      if (result) {
        setPlaceholders(result);
      }
    } catch (error) {
      console.error(error || "Error fetching notification templates.");
    }
  };

  useEffect(() => {
    getNotifications();
  }, []);

  useEffect(() => {
    if (templates.length > 0 && placeholders.length > 0) {
      const processedNotifications = placeholders.map((placeholder) =>
        processSingleNotification(placeholder)
      );

      const unread = processedNotifications.filter((pn) => !pn.isRead);
      const read = processedNotifications.filter((pn) => pn.isRead);

      setUnreadNotifications(unread);
      setReadNotifications(read);
      setNotifications(unread);
      setAllNotifications(processedNotifications);
    }
  }, [placeholders, templates]);

  useEffect(() => {
    if (notifications.length > 0) {
      setIsAllSelected(selectedNotifications.length === notifications.length);
    } else {
      setIsAllSelected(false);
    }
  }, [selectedNotifications, notifications]);

  const replacePlaceholders = (template, placeholder) => {
    if (!template) return "";

    let result = template;
    result = result.replace(/{EntityID1}/g, placeholder.entityID1 || "");
    result = result.replace(/{EntityID2}/g, placeholder.entityID2 || "");
    result = result.replace(/{EntityType1}/g, placeholder.entityType1 || "");
    result = result.replace(/{EntityType2}/g, placeholder.entityType2 || "");

    if (placeholder.placeholderText1) {
      result = result.replace(
        /{placeholderText1}/g,
        placeholder.placeholderText1
      );
    }
    if (placeholder.placeholderText2) {
      result = result.replace(
        /{placeholderText2}/g,
        placeholder.placeholderText2
      );
    }
    if (placeholder.placeholderDate1) {
      result = result.replace(
        /{placeholderDate1}/g,
        placeholder.placeholderDate1
      );
    }

    return result;
  };

  const handleFilterChange = useCallback((index) => {
    if (index === 0) {
      setNotifications(unreadNotifications);
    } else if (index === 1) {
      setNotifications(readNotifications);
    }
    setSelectedButton(index);
  }, [unreadNotifications, readNotifications]);

  const handleSelectNotification = useCallback((userNotificationID) => {
    setSelectedNotifications((prev) => {
      if (prev.includes(userNotificationID)) {
        return prev.filter((id) => id !== userNotificationID);
      } else {
        return [...prev, userNotificationID];
      }
    });
  }, []);

  const handleSelectAll = useCallback((checked) => {
    setIsAllSelected(checked);
    if (checked) {
      setSelectedNotifications(notifications.map((n) => n.userNotificationID));
    } else {
      setSelectedNotifications([]);
    }
  }, [notifications]);

  const handleMarkAsRead = useCallback(async () => {
    try {
      const results = await Promise.allSettled(
        selectedNotifications.map((sn) => MarkAsRead(sn, navigate))
      );

      const failedOperations = results.filter(
        (result) => result.status === "rejected"
      );
      if (failedOperations.length > 0) {
        console.error("Some operations failed:", failedOperations);
      } else {
        getNotifications();
        setNotifications((prev) =>
          prev.map((notification) =>
            selectedNotifications.includes(notification.notificationID)
              ? { ...notification, isRead: true }
              : notification
          )
        );

        setUnreadNotifications((prev) =>
          prev.filter(
            (notification) =>
              !selectedNotifications.includes(notification.notificationID)
          )
        );

        setReadNotifications((prev) => [
          ...prev,
          ...notifications.filter((notification) =>
            selectedNotifications.includes(notification.notificationID)
          ),
        ]);

        setSelectedNotifications([]);
      }
    } catch (error) {
      console.error("Error marking notifications as read:", error);
    }
  }, [selectedNotifications, notifications, unreadNotifications, readNotifications]);

  const NotificationProps = useMemo(() => ({
    notifications,
    unreadNotifications,
    readNotifications,
    selectedButton,
    selectedNotifications,
    isAllSelected,
    handleFilterChange,
    handleSelectNotification,
    handleSelectAll,
    handleMarkAsRead
  }), [
    notifications,
    unreadNotifications,
    readNotifications,
    selectedButton,
    selectedNotifications,
    isAllSelected,
    handleFilterChange,
    handleSelectNotification,
    handleSelectAll,
    handleMarkAsRead
  ]);

  return {
    NotificationProps,
  };
};
